/**
 * useExecutions — Hook for execution history, stats, and controlled polling.
 * No uncontrolled setInterval — uses setTimeout chains with cleanup.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useExecutions = () => {
  const [loading, setLoading] = useState(false);
  const activePolls = useRef(new Map()); // executionId → AbortController
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Abort all active polls on unmount
      activePolls.current.forEach(c => c.abort());
      activePolls.current.clear();
    };
  }, []);

  const queueExecution = useCallback(async (resultType, inputParams = {}, projectId = null) => {
    setLoading(true);
    try {
      const body = { result_type: resultType, input_params: inputParams };
      if (projectId) body.project_id = projectId;
      const res = await axios.post(`${API}/executions`, body);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const getExecution = useCallback(async (executionId, signal) => {
    const res = await axios.get(`${API}/executions/${executionId}`, { signal });
    return res.data.execution;
  }, []);

  /**
   * Poll execution with controlled setTimeout chain.
   * Returns cancel function. Stops on terminal state or cancel.
   */
  const pollExecution = useCallback((executionId, onUpdate, interval = 2500) => {
    // Cancel existing poll for this execution
    if (activePolls.current.has(executionId)) {
      activePolls.current.get(executionId).abort();
    }

    const controller = new AbortController();
    activePolls.current.set(executionId, controller);

    const doPoll = async () => {
      if (controller.signal.aborted || !mountedRef.current) return;

      try {
        const execution = await getExecution(executionId, controller.signal);
        if (!mountedRef.current || controller.signal.aborted) return;

        onUpdate(execution);

        const terminal = ['completed', 'failed', 'cancelled'];
        if (terminal.includes(execution.status)) {
          activePolls.current.delete(executionId);
          return;
        }

        // Schedule next poll (controlled)
        setTimeout(doPoll, interval);
      } catch (err) {
        if (err.name !== 'CanceledError') {
          console.error('[Executions] Poll error:', err.message);
          // Retry after delay on error
          if (!controller.signal.aborted) setTimeout(doPoll, interval * 2);
        }
      }
    };

    doPoll();

    return () => {
      controller.abort();
      activePolls.current.delete(executionId);
    };
  }, [getExecution]);

  const listExecutions = useCallback(async (limit = 50) => {
    const res = await axios.get(`${API}/executions`, { params: { limit } });
    return res.data.executions;
  }, []);

  const getStats = useCallback(async () => {
    const res = await axios.get(`${API}/executions/stats/summary`);
    return res.data.stats;
  }, []);

  const listProjectExecutions = useCallback(async (projectId, limit = 100) => {
    const res = await axios.get(`${API}/executions/project/${projectId}`, { params: { limit } });
    return res.data.executions;
  }, []);

  const listResultsByType = useCallback(async (resultType, limit = 50) => {
    const res = await axios.get(`${API}/executions/results/${resultType}`, { params: { limit } });
    return res.data.results;
  }, []);

  const getAssetCounts = useCallback(async () => {
    const res = await axios.get(`${API}/executions/assets/count`);
    return res.data;
  }, []);

  /** Hard stop: corta la generación en Replicate, no se cobra y se descarta. */
  const stopExecution = useCallback(async (executionId) => {
    const res = await axios.post(`${API}/executions/${executionId}/stop`);
    return res.data;
  }, []);

  return {
    loading,
    queueExecution,
    getExecution,
    pollExecution,
    listExecutions,
    getStats,
    listProjectExecutions,
    listResultsByType,
    getAssetCounts,
    stopExecution,
  };
};

export default useExecutions;
